/**
 * The 290px dark-teal App Grid Control panel: the "Most Popular" badge toggle,
 * the 2×2 drop slots, catalogue stats, a mini phone preview and the legend.
 * Ported from the LifeScribe original.
 *
 * One stat tile differs: the original's fourth tile was "Total Usage", summed
 * from per-row usage counters that are always zero. This shows WITHHELD (rows
 * carrying an `unavailable_reason`) instead — a number the API actually returns.
 */

import React from 'react';
import { Box, Typography, ToggleButtonGroup, ToggleButton } from '@mui/material';
import { colors } from '../../../theme/designTokens';
import GridDropSlot from './GridDropSlot';
import { MAX_ACTIVE_PER_CATALOGUE } from './constants';
import { OrderItem, StyleFamily } from './types';

export interface AppGridControlStats {
  inGrid: number;
  total: number;
  inactive: number;
  withheld: number;
}

export interface AppGridControlPanelProps {
  activeItems: OrderItem[];
  stats: AppGridControlStats;
  isMostPopularCatalogue: boolean;
  family: StyleFamily;
  popularValue: string;
  onTogglePopular: (family: StyleFamily, value: string) => void;
}

const AppGridControlPanel: React.FC<AppGridControlPanelProps> = ({
  activeItems,
  stats,
  isMostPopularCatalogue,
  family,
  popularValue,
  onTogglePopular,
}) => {
  // ── F12 (2026-08-03 audit): show every active row, not the first four ──────
  //
  // This used to be `activeItems.slice(0, MAX_ACTIVE_PER_CATALOGUE)`. The admin
  // API refuses to ACTIVATE a fifth (409 MAX_ACTIVE_REACHED), so four looked
  // like a safe assumption — but the LifeScribe authors import wrote
  // `is_active = TRUE` straight past that gate, and production ended up with
  // five active authors. The fifth was invisible here while the member API
  // offered it, so the panel showed a catalogue that did not exist.
  //
  // The panel now renders every active row and says so when there are more than
  // the grid is designed for. An over-limit catalogue is a real state that an
  // operator has to fix; hiding the row is how it went unnoticed.
  const overLimit = activeItems.length > MAX_ACTIVE_PER_CATALOGUE;
  const slotCount = Math.max(MAX_ACTIVE_PER_CATALOGUE, activeItems.length);
  const slots: (OrderItem | null)[] = Array.from(
    { length: slotCount },
    (_, i) => activeItems[i] || null,
  );

  const options =
    family === 'writing'
      ? [
          { value: 'genre', label: 'Genres' },
          { value: 'author', label: 'Authors' },
        ]
      : [
          { value: 'style', label: 'Styles' },
          { value: 'artist', label: 'Artists' },
        ];

  return (
    <Box
      sx={{
        bgcolor: '#0d3d50',
        width: { xs: '100%', md: 290 },
        flexShrink: 0,
        overflowY: 'auto',
        p: 2.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 2.5,
        borderLeft: '1px solid rgba(255,255,255,0.06)',
      }}
    >
      <Typography
        sx={{
          fontFamily: '"Playfair Display", serif',
          fontStyle: 'italic',
          fontSize: '1.06rem',
          color: '#fff',
          fontWeight: 700,
          letterSpacing: '0.01em',
        }}
      >
        App Grid Control
      </Typography>

      <Box>
        <Typography
          sx={{
            fontFamily: '"Montserrat", sans-serif',
            fontSize: '0.6rem',
            color: 'rgba(255,255,255,0.55)',
            fontWeight: 600,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            mb: 0.6,
          }}
        >
          Most Popular Badge
        </Typography>
        {/* F8, resolved. The audit found this setting had ZERO readers — it was
            written to quill_admin_settings and drawn only in the preview below,
            so the control changed what an admin saw and nothing else. It is now
            wired through: quill-engine's GET /api/quill/genres and
            GET /api/quill/illustration-styles name the badged row
            (`most_popular_genre_key` / `most_popular_style_key`) and the member's
            picker draws "Most popular" on it.

            The copy names the FIRST row deliberately: that is what this panel
            already draws it on (slot 1 / activeOrdered[0]) and what the engine
            resolves the setting to, so the two surfaces cannot drift.

            "From the next app release" is not a hedge — a shipped build has no
            code to draw the badge, and this panel may only claim what the code
            does. Drop that clause once the build carrying it is live. */}
        <Typography
          data-testid="most-popular-preview-note"
          sx={{
            fontFamily: '"Montserrat", sans-serif',
            fontSize: '0.62rem',
            color: 'rgba(255,255,255,0.45)',
            lineHeight: 1.45,
            mb: 0.6,
          }}
        >
          Saved. Members see &ldquo;Most popular&rdquo; on the first{' '}
          {family === 'writing' ? 'writing style' : 'illustration style'} of the catalogue you
          pick here, from the next app release. Older installs show no badge. It is your
          editorial choice, not a usage figure.
        </Typography>
        <ToggleButtonGroup
          value={popularValue}
          exclusive
          onChange={(_e, val) => {
            if (val) onTogglePopular(family, val);
          }}
          size="small"
          aria-label="Most Popular badge catalogue"
          sx={{ width: '100%', display: 'flex' }}
        >
          {options.map((opt) => (
            <ToggleButton
              key={opt.value}
              value={opt.value}
              sx={{
                flex: 1,
                fontFamily: '"Montserrat", sans-serif',
                fontSize: '0.7rem',
                fontWeight: 600,
                textTransform: 'none',
                color: 'rgba(255,255,255,0.6)',
                borderColor: 'rgba(255,255,255,0.15)',
                '&.Mui-selected': {
                  bgcolor: colors.warmGold[500],
                  color: '#fff',
                  '&:hover': { bgcolor: colors.warmGold[500] },
                },
                '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
              }}
            >
              {opt.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {overLimit && (
        <Box
          data-testid="app-grid-over-limit"
          sx={{
            bgcolor: 'rgba(239,68,68,0.14)',
            border: '1px solid rgba(239,68,68,0.45)',
            borderRadius: '6px',
            p: 1,
          }}
        >
          <Typography
            sx={{
              fontFamily: '"Montserrat", sans-serif',
              fontSize: '0.68rem',
              color: '#fecaca',
              fontWeight: 700,
              lineHeight: 1.45,
            }}
          >
            {activeItems.length} active — the grid holds {MAX_ACTIVE_PER_CATALOGUE}
          </Typography>
          <Typography
            sx={{
              fontFamily: '"Montserrat", sans-serif',
              fontSize: '0.64rem',
              color: 'rgba(255,255,255,0.75)',
              lineHeight: 1.45,
              mt: 0.4,
            }}
          >
            Every active row is shown below, but members are offered all of them while this panel
            is designed for {MAX_ACTIVE_PER_CATALOGUE}. Deactivate one to bring the catalogue back
            in line.
          </Typography>
        </Box>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
        {slots.map((item, index) => (
          <GridDropSlot
            key={index}
            index={index}
            item={item}
            isMostPopular={index === 0 && isMostPopularCatalogue}
          />
        ))}
      </Box>

      <Typography
        sx={{
          fontFamily: '"Montserrat", sans-serif',
          fontSize: '0.72rem',
          color: 'rgba(255,255,255,0.55)',
          textAlign: 'center',
          lineHeight: 1.5,
        }}
      >
        Drag cards from the left panel into slots to reorder the app grid
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
        {[
          { label: 'In Grid', value: stats.inGrid, color: colors.warmGold[500] },
          { label: 'Total', value: stats.total, color: '#fff' },
          { label: 'Inactive', value: stats.inactive, color: '#ef4444' },
          { label: 'Withheld', value: stats.withheld, color: '#f97316' },
        ].map((stat) => (
          <Box
            key={stat.label}
            sx={{ bgcolor: 'rgba(255,255,255,0.04)', borderRadius: '6px', p: 1, textAlign: 'center' }}
          >
            <Typography
              sx={{
                fontFamily: '"Montserrat", sans-serif',
                fontWeight: 700,
                fontSize: '1.2rem',
                color: stat.color,
              }}
            >
              {stat.value}
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Montserrat", sans-serif',
                fontSize: '0.66rem',
                color: 'rgba(255,255,255,0.65)',
                fontWeight: 600,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
              }}
            >
              {stat.label}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Mini phone preview */}
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Box
          sx={{
            width: 140,
            bgcolor: '#0a2e3d',
            borderRadius: '14px',
            p: 1,
            border: '2px solid rgba(255,255,255,0.08)',
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 4,
              bgcolor: 'rgba(255,255,255,0.1)',
              borderRadius: 2,
              mx: 'auto',
              mb: 1,
            }}
          />
          <Box sx={{ bgcolor: '#f5f0e8', borderRadius: '8px', p: 0.8 }}>
            <Typography
              sx={{
                fontFamily: '"Montserrat", sans-serif',
                fontSize: '0.4rem',
                fontWeight: 700,
                color: '#1e293b',
                textAlign: 'center',
                mb: 0.6,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
              }}
            >
              Choose Style
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3px' }}>
              {slots.map((item, i) => (
                <Box
                  key={i}
                  sx={{
                    bgcolor: item ? '#fff' : '#e5e7eb',
                    borderRadius: '4px',
                    p: 0.4,
                    border: item
                      ? i === 0 && isMostPopularCatalogue
                        ? `1px solid ${colors.warmGold[500]}`
                        : '1px solid #e2e8f0'
                      : '1px dashed #d1d5db',
                    minHeight: 22,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {item ? (
                    <Typography
                      sx={{
                        fontFamily: '"Montserrat", sans-serif',
                        fontSize: '0.32rem',
                        fontWeight: 600,
                        color: '#475569',
                        textAlign: 'center',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {item.name.length > 8 ? `${item.name.slice(0, 8)}...` : item.name}
                    </Typography>
                  ) : (
                    <Box sx={{ width: 8, height: 1, bgcolor: '#d1d5db', borderRadius: 1 }} />
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Legend */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
        <Typography
          sx={{
            fontFamily: '"Montserrat", sans-serif',
            fontSize: '0.66rem',
            color: 'rgba(255,255,255,0.65)',
            fontWeight: 600,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            mb: 0.3,
          }}
        >
          Legend
        </Typography>
        {[
          { color: colors.warmGold[500], label: 'Most Popular (Position #1)', border: 'solid' },
          { color: 'rgba(255,255,255,0.5)', label: 'Active in grid', border: 'solid' },
          { color: 'rgba(255,255,255,0.2)', label: 'Empty slot', border: 'dashed' },
        ].map((entry) => (
          <Box key={entry.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '3px',
                border: `1.5px ${entry.border} ${entry.color}`,
                flexShrink: 0,
              }}
            />
            <Typography
              sx={{
                fontFamily: '"Montserrat", sans-serif',
                fontSize: '0.66rem',
                color: 'rgba(255,255,255,0.65)',
              }}
            >
              {entry.label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default AppGridControlPanel;
